
import { Component, OnInit, OnDestroy, Input, Inject } from "@angular/core";

import { AlertService, MessageSeverity, DialogType } from '../../services/alert.service';
import { AuthService } from "../../services/auth.service";
import { AccountService } from "../../services/account.service";
import { ConfigurationService } from '../../services/configuration.service';
import { Utilities } from '../../services/utilities';
import { UserLogin } from '../../models/user-login.model';
import { DOCUMENT } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import Success = MessageSeverity.Success;
import Error = MessageSeverity.Error;
import Error1 = MessageSeverity.Error;
import Error2 = MessageSeverity.Error;
import Error3 = MessageSeverity.Error;
import Warn = MessageSeverity.Warn;
import Prompt = DialogType.Prompt;

// Captured at module-evaluation time, before Angular bootstraps or the router runs any guard -
// deliberately NOT read via ActivatedRoute in ngOnInit. The root route ("") redirects to
// "dashboard", which is guarded by AuthGuard; when the user isn't logged in (always true here,
// since crm-login only renders pre-login), AuthGuard.checkLogin navigates to "/login" with no
// queryParams at all, stripping ?email=&token= from the URL before this component ever gets a
// chance to read them from the router. Reading the browser's actual initial URL directly is
// immune to that redirect entirely.
const initialResetLinkParams = new URLSearchParams(window.location.search);

@Component({
  selector: "crm-login",
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})

export class LoginComponent implements OnInit, OnDestroy {

  userLogin = new UserLogin();
  isLoading = false;
  formResetToggle = true;
  mfaStep = false;
  mfaTicket: string;
  mfaCode = "";
  trustDeviceChecked = true;
  passwordChangeStep = false;
  changeTicket: string;
  newPassword = "";
  confirmNewPassword = "";
  forgotPasswordStep = false;
  forgotPasswordEmail = "";
  resetPasswordStep = false;
  resetPasswordEmail: string;
  resetPasswordToken: string;
  resetPasswordSetup = false;   // the link is a new account's "create your password" invitation, not a reset
  resetNewPassword = "";
  resetConfirmNewPassword = "";
  modalClosedCallback: () => void;
  loginStatusSubscription: any;
  private profilePic = require("../../assets/images/demo/avatars/1.jpg");
  public logo = require("../../assets/images/demo/il_login.svg");
  public loginPic = require("../../assets/images/demo/bg_login.svg");
  returnUrl: string;
  @Input()
  isModal = false;


  constructor(private alertService: AlertService,
    private authService: AuthService,
    private accountService: AccountService,
    private route: ActivatedRoute,
    private router: Router,
    private configurations: ConfigurationService,
    @Inject(DOCUMENT) private document: Document) {


  }


  ngOnInit() {
    this.document.body.classList.add('page-signin');
    this.userLogin.rememberMe = this.authService.rememberMe;

    const resetEmail = initialResetLinkParams.get('email');
    const resetToken = initialResetLinkParams.get('token');
    if (resetEmail && resetToken) {
      this.resetPasswordEmail = resetEmail;
      this.resetPasswordToken = resetToken;
      this.resetPasswordSetup = initialResetLinkParams.get('setup') === '1';
      this.resetPasswordStep = true;
      return;
    }

    if (this.getShouldRedirect()) {
      this.authService.redirectLoginUser();
    }
    else {
      this.loginStatusSubscription = this.authService.getLoginStatusEvent().subscribe(isLoggedIn => {
        if (this.getShouldRedirect()) {
          this.authService.redirectLoginUser();
        }
      });
    }
  }


  ngOnDestroy() {
    if (this.loginStatusSubscription)
      this.loginStatusSubscription.unsubscribe();
  }


  getShouldRedirect() {
    return !this.isModal && this.authService.isLoggedIn && !this.authService.isSessionExpired;
  }


  showErrorAlert(caption: string, message: string) {
    this.alertService.showMessage(caption, message, Error);
  }

  closeModal() {
    if (this.modalClosedCallback) {
      this.modalClosedCallback();
    }
  }


  login() {
    this.isLoading = true;
    this.alertService.startLoadingMessage("", "Attempting login...");

    this.authService.login(this.userLogin.email, this.userLogin.password, this.userLogin.rememberMe)
      .subscribe(
        (result: any) => {

          this.alertService.stopLoadingMessage();
          this.isLoading = false;

          if (result && result.mfaRequired) {
            this.mfaTicket = result.mfaTicket;
            this.mfaStep = true;
            this.alertService.showMessage("Verification required", "We've emailed you a verification code. Please enter it below to finish signing in.", Success);
            return;
          }

          if (result && result.passwordChangeRequired) {
            this.changeTicket = result.changeTicket;
            this.passwordChangeStep = true;
            this.alertService.showMessage("Password change required", "Your password must be changed before you can continue.", Warn);
            return;
          }

          this.reset();
          this.document.body.classList.remove('page-signin');

          this.alertService.showMessage("Login", `Welcome ${result.userName}!`, Success);
          this.authService.redirectLoginUser();
        },
        error => {

          this.alertService.stopLoadingMessage();

          if (Utilities.checkNoNetwork(error)) {
            this.alertService.showStickyMessage(Utilities.noNetworkMessageCaption, Utilities.noNetworkMessageDetail, Error1, error);
            this.offerAlternateHost();
          }
          else {
            let errorMessage = Utilities.findHttpResponseMessage("error_description", error);

            if (errorMessage)
              this.alertService.showStickyMessage("Unable to login", errorMessage, Error2, error);
            else
              this.alertService.showStickyMessage("Unable to login", "An error occured whilst logging in, please try again later.\nError: " + (Utilities.getResponseBody(error) ? Utilities.getResponseBody(error).errorDescription :""), Error3, error);
          }

          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        });
  }


  verifyMfaCode() {
    this.isLoading = true;
    this.alertService.startLoadingMessage("", "Verifying...");

    this.authService.verifyMfaCode(this.mfaTicket, this.mfaCode, this.userLogin.rememberMe)
      .subscribe(
        (result: any) => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.mfaStep = false;

          if (result && result.passwordChangeRequired) {
            this.changeTicket = result.changeTicket;
            this.passwordChangeStep = true;
            this.alertService.showMessage("Password change required", "Your password must be changed before you can continue.", Warn);
            return;
          }

          this.reset();
          this.document.body.classList.remove('page-signin');

          if (this.trustDeviceChecked) {
            this.authService.trustThisDevice().subscribe(() => { }, () => { });
          }

          this.alertService.showMessage("Login", `Welcome ${result.userName}!`, Success);
          this.authService.redirectLoginUser();
        },
        error => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.mfaCode = "";

          let errorMessage = Utilities.findHttpResponseMessage("error_description", error);
          this.alertService.showStickyMessage("Verification failed", errorMessage || "The code is incorrect or has expired. Please try again or log in again.", Error2, error);
        });
  }


  completePasswordChange() {
    if (this.newPassword !== this.confirmNewPassword) {
      this.alertService.showMessage("Passwords don't match", "Please make sure both password fields match.", Warn);
      return;
    }

    this.isLoading = true;
    this.alertService.startLoadingMessage("", "Updating password...");

    this.authService.completePasswordChange(this.changeTicket, this.newPassword, this.userLogin.rememberMe)
      .subscribe(
        user => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.passwordChangeStep = false;
          this.reset();
          this.document.body.classList.remove('page-signin');

          this.alertService.showMessage("Login", `Welcome ${user.userName}!`, Success);
          this.authService.redirectLoginUser();
        },
        error => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.newPassword = "";
          this.confirmNewPassword = "";

          let errorMessage = Utilities.findHttpResponseMessage("error_description", error);
          this.alertService.showStickyMessage("Unable to update password", errorMessage || "Please try again or log in again.", Error2, error);
        });
  }


  cancelPasswordChangeStep() {
    this.passwordChangeStep = false;
    this.changeTicket = null;
    this.newPassword = "";
    this.confirmNewPassword = "";
    this.userLogin.password = "";
  }


  cancelMfaStep() {
    this.mfaStep = false;
    this.mfaTicket = null;
    this.mfaCode = "";
    this.userLogin.password = "";
  }


  showForgotPasswordStep() {
    this.forgotPasswordEmail = this.userLogin.email || "";
    this.forgotPasswordStep = true;
  }


  cancelForgotPasswordStep() {
    this.forgotPasswordStep = false;
    this.forgotPasswordEmail = "";
  }


  requestPasswordReset() {
    this.isLoading = true;
    this.alertService.startLoadingMessage("", "Sending reset link...");

    this.accountService.forgotPassword(this.forgotPasswordEmail)
      .subscribe(
        () => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.forgotPasswordStep = false;
          this.alertService.showMessage("Check your email", "If that email address is registered, a password reset link has been sent.", Success);
        },
        error => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.alertService.showStickyMessage("Unable to send reset email", "Please try again later.", Error2, error);
        });
  }


  completePasswordReset() {
    if (this.resetNewPassword !== this.resetConfirmNewPassword) {
      this.alertService.showMessage("Passwords don't match", "Please make sure both password fields match.", Warn);
      return;
    }

    this.isLoading = true;
    this.alertService.startLoadingMessage("", "Updating password...");

    this.accountService.resetPasswordViaToken(this.resetPasswordEmail, this.resetPasswordToken, this.resetNewPassword, this.resetPasswordSetup)
      .subscribe(
        () => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.resetPasswordStep = false;
          this.resetNewPassword = "";
          this.resetConfirmNewPassword = "";
          window.history.replaceState(null, "", window.location.pathname);
          if (this.resetPasswordSetup)
            this.alertService.showMessage("Password created", "Your account is ready. Please log in with your username and new password.", Success);
          else
            this.alertService.showMessage("Password updated", "Your password has been changed. Please log in with your new password.", Success);
          this.resetPasswordSetup = false;
        },
        error => {
          this.alertService.stopLoadingMessage();
          this.isLoading = false;
          this.resetNewPassword = "";
          this.resetConfirmNewPassword = "";
          this.alertService.showStickyMessage("Save Error", "The below errors occured whilst resetting your password:", Error2, error);
          this.alertService.showStickyMessage(error, null, Error2);
        });
  }


  cancelResetPasswordStep() {
    this.resetPasswordStep = false;
    this.resetPasswordEmail = null;
    this.resetPasswordToken = null;
    this.resetPasswordSetup = false;
    this.resetNewPassword = "";
    this.resetConfirmNewPassword = "";
    window.history.replaceState(null, "", window.location.pathname);
  }


  offerAlternateHost() {

    if (Utilities.checkIsLocalHost(location.origin) && Utilities.checkIsLocalHost(this.configurations.baseUrl)) {
      this.alertService.showDialog("Dear Developer!\nIt appears your backend Web API service is not running...\n" +
        "Would you want to temporarily switch to the online Demo API below?(Or specify another)",
        Prompt,
        (value: string) => {
          this.configurations.baseUrl = value;
          this.alertService.showStickyMessage("API Changed!", "The target Web API has been changed to: " + value, Warn);
        },
        null,
        null,
        null,
        this.configurations.fallbackBaseUrl);
    }
  }


  reset() {
    this.formResetToggle = false;

    setTimeout(() => {
      this.formResetToggle = true;
    });
  }
}
